'use client';

import {
  NotificationTooltip,
  useNotificationState,
} from '@/components/notification/notification';
import {usePathname} from 'next/navigation';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  type Step,
  getStepPathFromRequestPath,
  stepsList,
} from '../StepsRouting';
import {useGoToNextStep, useGoToPreviousStep} from './useLoadStep';
import {useGetStepByPath} from './useWidget';

export * from './useLoadStep';
export * from './useWidget';

export interface FooterButton {
  label: string;
  isPrimary?: boolean;
  customHandler?: string; // TODO: make an enum ?
}

export interface Footer {
  buttons: FooterButton[];
}

export enum LoadingState {
  NotInitilized = 'NotInitilized',
  Loading = 'Loading',
  Loaded = 'Loaded',
  Error = 'Error',
}

export interface StepsWizardState {
  currentStep?: Step;

  /**
   * Go to the next step.
   * @deprecated Stay there for backward compatability, will not be tracked by isNextPageLoading
   * Better use goToNextPage
   */
  nextStep: (step?: string) => Promise<void>;
  /** Go to next page based on current step and track it with isNextPageLoading */
  goToNextPage: () => void;
  nextPageLoadingState: LoadingState;
  isNextPageLoading: boolean;
  /**
   * Go to the previous step
   */
  previousStep: () => void;

  /** The current active step of the wizard */
  activeStepIndex: number;
  /** The total number of steps of the wizard */
  stepCount: number;
  /** Indicate if the current step is the last step (aka no next step) */
  isLastStep: boolean;
}

const defaultState: StepsWizardState = {
  currentStep: undefined,
  nextStep: () => Promise.resolve(),
  goToNextPage: () => {},
  nextPageLoadingState: LoadingState.NotInitilized,
  isNextPageLoading: false,

  previousStep: () => {},

  activeStepIndex: 0,
  stepCount: 0,
  isLastStep: false,
};

export const StepsWizardContext = createContext<StepsWizardState>(defaultState);
/**
 * Provides states for current step of wizard and way to manipualte him
 * Uses NextJS router for sitching pages
 * TODO: migrate from centralized routes to choreography routing. Each page must know by route which next page.
 */
export const useWizard = () => useContext(StepsWizardContext);

export const StepsWizardProvider = ({children}: PropsWithChildren) => {
  const pathname = usePathname();
  const stepPath = getStepPathFromRequestPath(pathname);

  // use browser router as sourse of truth for current step
  const [currentStep, activeStepIndex, isLastStep] = useGetStepByPath(stepPath);

  const {
    isShowingNotification: isShowingNextStepError,
    showNotification: showNextStepError,
    dismissNotification: dismissError,
  } = useNotificationState();

  const nextStepWithoutErrorHandler = useGoToNextStep(currentStep);
  const nextStep = useCallback(
    async (step?: string, onError?: (error: any) => void) => {
      return await nextStepWithoutErrorHandler(step, (error: any) => {
        showNextStepError();
        onError?.(error);
      });
    },
    [nextStepWithoutErrorHandler, showNextStepError],
  );

  const [state, goToNextPage] = useIsPageLoading(activeStepIndex, nextStep);

  return (
    <StepsWizardContext.Provider
      value={{
        stepCount: stepsList.length,
        activeStepIndex,
        isLastStep,
        currentStep,
        nextStep,
        goToNextPage,
        nextPageLoadingState: state,
        isNextPageLoading: state === LoadingState.Loading,
        previousStep: useGoToPreviousStep(currentStep),
      }}
    >
      {children}

      <NotificationTooltip
        severity="message"
        open={isShowingNextStepError}
        onClose={dismissError}
      >
        <span>
          An error occurred while processing your request. Please try again.
        </span>
      </NotificationTooltip>
    </StepsWizardContext.Provider>
  );
};

export const useIsPageLoading = (
  activeStep: number,
  nextStep: (step?: string, onError?: (error: any) => void) => Promise<void>,
): [LoadingState, () => void] => {
  const [state, setLoadingState] = useState(LoadingState.NotInitilized);
  const goToNextPage = useCallback(async () => {
    setLoadingState(LoadingState.Loading);

    // will check error only after execution to prevent race condition
    let error = undefined;
    await nextStep(undefined, e => {
      error = e || true;
    });

    setLoadingState(error ? LoadingState.Error : LoadingState.Loaded);
  }, [nextStep]);

  useEffect(() => {
    // when page changes reset loading
    setLoadingState(LoadingState.NotInitilized);
  }, [activeStep]);

  return [state, goToNextPage];
};
